import styled, { css } from 'styled-components';
import { JRCColumn } from './JRCColumn';
import { getStyle, MAX_WIDTH, PADDING_BOTTOM, PADDING_TOP, STYLE, TIMING } from './template.utils';
import { DashedFocusBorder } from '../Common/DashedFocusBorder';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import React from 'react';
import type { Closing, Open, LayoutMode, ColumnPosition } from './template.type';
import { JRCConditionalTooltip } from '../Common/JRCConditionalWrapper';
import { FlexDirection } from '../JRCFlex/JRCFlexBox';
import { Colors } from '../../styles/theme';

export const BasicAutoMarginWrapper = styled.div`
    display: flex;
    height: var(--body-height);
    margin-right: 16px;
    margin-left: 16px;
    gap: 18px;

    @media (width >= ${MAX_WIDTH}) {
        min-width: ${MAX_WIDTH};
        margin: auto;
    }
`;

// JRCContainer
export const ContainerWrapper = styled.div<{ $flexDirection?: FlexDirection; backgroundColor?: Colors }>`
    display: flex;
    flex-direction: ${(props) => props.$flexDirection ?? 'row'};
    align-items: stretch;
    flex: 1;
    height: var(--body-height);
    max-width: 100vw;
    position: relative;
    ${({ backgroundColor, theme }) => backgroundColor && `background-color: ${theme.color[backgroundColor]};`}
`;

export const ScrollWrapper = styled.div<{ $flexDirection?: FlexDirection }>`
    display: flex;
    flex-direction: ${(props) => props.$flexDirection ?? 'row'};
    align-items: stretch;
    flex: 1;
    overflow: auto;
`;

export const MaxWidthWrapper = styled.div<{
    $mode: LayoutMode;
    $maxWidth?: string;
    $flexDirection?: FlexDirection;
    $hasPadding?: boolean;
}>`
    width: 100%;
    flex: 1;
    margin: ${(props) => (props.$mode === 'centerTop' ? '0 auto' : 'auto')};
    position: relative;
    display: flex;
    flex-direction: ${(props) => props.$flexDirection ?? 'row'};
    align-items: stretch;

    --template-padding: 0px;
    --template-border-width: 0;
    --template-border-radius: 0;

    ${(props) =>
        (props.$mode === 'center' || props.$mode === 'centerTop') &&
        `
        max-width: ${props.$maxWidth ?? MAX_WIDTH};
        @media (min-width: ${props.$maxWidth ?? MAX_WIDTH}) {
            & {
                --template-padding: 32px;
                --template-border-width: 1px;
                --template-border-radius: 8px;
            }
        }
    `}
    ${(props) => props.$hasPadding && `--template-padding: 32px;`}

    gap: var(--template-padding);
    padding: var(--template-padding);
    --body-container-height: calc(var(--body-height) - 2 * var(--template-padding));
`;

export const PanelContainer = styled.div`
    overflow: hidden;
`;

// JRCColumn

export const BaseColumn = styled.div<{ $width: number; $position?: ColumnPosition }>`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    ${(props) =>
        Boolean(props?.$width) &&
        `
        width: ${props.$width}px;
        max-width: ${props.$width}px;
        min-width: ${props.$width}px;
    `};
`;

export const Panel = styled.div<{ width: number; open: boolean }>`
    height: 100%;
    display: flex;
    flex-direction: column;
    ${(props) => Boolean(props?.width) && `width: ${props.width}px;`};
    transition: margin ${TIMING}ms;
    margin: 0;
    background-color: white;
    border-left: 1px solid ${(props) => props.theme.color.grey2};

    ${(props) => !props.open && `margin-right: -${props.width}px;`};
`;

export const StickyColWrapper = styled.div<{ $mode: LayoutMode; $position?: ColumnPosition; $padding: string }>`
    transition: padding ${TIMING}ms;
    min-height: var(--body-container-height);
    position: sticky;
    bottom: var(--template-padding);
    padding: ${(props) => (props?.$padding ? props.$padding : `${PADDING_TOP} 16px ${PADDING_BOTTOM}`)};
    background-color: white;
    border-radius: var(--template-border-radius);

    ${(props) => props.$position === 'left' && `border-right: 1px solid ${props.theme.color.grey2};`}
    ${(props) => props.$position === 'right' && `border-left: 1px solid ${props.theme.color.grey2};`}

    ${(props) =>
        props.$mode === 'center' &&
        `
        @media (min-width: 1440px) {
            border: 1px solid ${props.theme.color.grey2};
        }
    `}
`;

// JRCMainColumn

export const MainColumn = styled(JRCColumn)`
    transition: all ${TIMING}ms;
    flex-grow: 1;

    ${(props) =>
        props.flex &&
        `
        display:flex;
        flex-direction: column;
    `}
    ${(props) =>
        props.mode === 'center' &&
        `
        @media (min-width: 1440px) {
            & {
                margin-left: 0px;
            }
        }
    `}
    .dimension {
        position: static;
        flex-grow: 1;

        ${(props) =>
            props.flex &&
            `
            display:flex;
            flex-direction: column;
        `}
    }
`;

// JRCAppColumn

export const AppColumn = styled(JRCColumn)<Open>`
    transition: width ${TIMING}ms;
    width: ${(props) => getStyle(props.open).width};
    z-index: 2;
    background-color: transparent;

    position: relative;

    flex-shrink: 0;

    & > .dimension {
        transition: padding ${TIMING}ms;
        padding-left: ${(props) => getStyle(props.open).padding};
        padding-right: ${(props) => getStyle(props.open).padding};

        background-color: white;
    }

    :hover .open-close {
        opacity: 1;
        transition-delay: 0ms;
    }
`;

export const FlexDescription = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

export const EasterEggIcon = styled(JRCIcon)<{ easterEgg: boolean }>`
    ${(props) =>
        props.easterEgg &&
        `& {
        animation: blinker 0.5s linear infinite;
        background: linear-gradient(rgba(255,0,0,1) 0%, rgba(255,154,0,1) 10%, rgba(208,222,33,1) 20%, rgba(79,220,74,1) 30%, rgba(63,218,216,1) 40%, rgba(47,201,226,1) 50%, rgba(28,127,238,1) 60%, rgba(95,21,242,1) 70%, rgba(186,12,248,1) 80%, rgba(251,7,217,1) 90%, rgba(255,0,0,1) 100%) 0 0/100% 200%;
    }`}

    @keyframes blinker {
        to {
            background-position: 0 -200%;
        }
    }
`;

export const reduce = css<Open>`
    transition: all ${TIMING}ms;
    max-height: ${(props) => (props.open ? '150px' : '0')};
    overflow: hidden;
    margin: 0;
`;

export const AppTitle = styled.h1`
    font-size: 18px;
    line-height: 1.5em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

export const DisappearP = styled.p`
    ${reduce};
    width: calc(${STYLE.open.width} - 2 * ${STYLE.open.padding});
`;

export const DisappearSpan = styled.span<Open>`
    transition: all ${TIMING}ms;
    max-width: ${(props) => (props.open ? `calc(${STYLE.open.width} - 2 * ${STYLE.open.padding})` : '0')};
    opacity: ${(props) => (props.open ? '1' : '0')};
    overflow: hidden;
    margin-bottom: ${(props) => (props.open ? '8px' : '0')};
    display: block;
`;

export function DisappearDescription({ children, open }: { children: any; open: boolean }) {
    return (
        <DisappearSpan open={open}>
            <DisappearP open={open}>{children}</DisappearP>
        </DisappearSpan>
    );
}

export const OpenCloseButton = styled.button.attrs((props) => ({ offset: '3px', ...props }))<Open & Closing>`
    transition: all ${TIMING}ms, opacity 500ms;
    background-color: ${(props) => props.theme.color.secondary};
    width: 20px;
    height: 20px;
    border-radius: 10px;

    position: absolute;
    right: -10px;
    top: calc(14px + ${(props) => getStyle(props.open).iconSize}px / 2);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        margin: 0 2px 0 0;
    }

    :focus-visible {
        opacity: 1;
    }

    ${(props) => DashedFocusBorder({ offset: props.offset })};

    transform: scale(-1, 1);
    ${(props) => props.open && `transform: scale(1, 1)`};
`;

export const AppTabLabel = styled.p<Open>`
    transition: all ${TIMING}ms;
    opacity: ${(props) => (props.open ? '1' : '0')};
    padding: ${(props) => (props.open ? '16px 0 4px' : '0')};
    font-size: 12px;
`;

export const AppTabContainer = styled.div<Open>`
    transition: all ${TIMING}ms;
    outline: 1px solid ${(props) => (props.open ? props.theme.color.grey2 : 'white')};
    border-radius: 8px;
    overflow: hidden;
`;

const AppTabHoverStyle = css`
    background-color: ${(props) => props.theme.color.grey1};
    text-decoration: none;
    color: ${(props) => props.theme.color.secondary};
`;

export const AppTabContextContainer = styled.div<Open & Closing>`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.open || props.closing ? 'flex-start' : 'center')};
    overflow: hidden;
    flex: 1;
`;

// JRCAppTab
export const AppTab = styled.button<Open & { active?: boolean }>`
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    transition: all ${TIMING}ms, background-color 0.2s, color 0.2s;
    color: ${(props) => props.theme.color.grey4};
    overflow: hidden;
    min-width: 30px;

    ${(props) =>
        props.open &&
        `
        padding: 0 8px;
        width: 100%;
        height: 40px;

        &:first-child {
            border-radius: 8px 8px 0 0; 
        }
        &:last-child {
            border-radius: 0 0 8px 8px; 
        }
    `}
    ${(props) =>
        !props.open &&
        `
        padding: 0 4px;
        margin: 3px 0;
        border-radius: 8px;
        height: 30px;
        width: 30px;
    `}
    &:hover, &:focus-visible {
        ${AppTabHoverStyle};
    }

    ${(props) => props.active && AppTabHoverStyle};
    ${(props) => props.active && 'cursor: default;'};

    ${DashedFocusBorder()}
`;

export const AppTabTooltip = styled(JRCConditionalTooltip)`
    display: flex;
    flex: 1;

    > span {
        display: flex;
        flex: 1;
    }
`;

export const EllipsisWrapper = styled.span<Open>`
    display: flex;
    margin: 0px;
    transition: all ${TIMING}ms, color 0.2s;
    opacity: ${(props) => (props.open ? 1 : 0)};
`;

export const AppTabCounter = styled.span`
    align-items: center;
    display: flex;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    margin-left: 4px;
    overflow: hidden;

    animation: opacity 1s ease-in;
    @keyframes opacity {
        0% {
            width: 0;
            opacity: 0;
        }
        33% {
            width: 0;
            opacity: 0;
        }
        100% {
            width: auto;
            opacity: 1;
        }
    }
`;

export const AppTabEllipsisLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-left: 4px;
`;

export const AppTabNav = styled.nav<Open & { extraMargin: boolean }>`
    transition: all ${TIMING}ms;
    ${(props) => props.extraMargin && `padding-top: ${props.open ? '24px' : '16px'}`};
`;
